<template>
  <el-dialog
    :model-value="visible"
    :title="modalTitle"
    width="520px"
    destroy-on-close
    :close-on-click-modal="false"
    @update:model-value="onVisibleChange"
  >
    <div v-loading="status.isLoading" :element-loading-text="status.spinnerMessage">
      <template v-if="refundInfo">
        <div class="brlv-upgrade-modal__cards">
          <div class="brlv-upgrade-modal__card brlv-upgrade-modal__card--current">
            <span class="brlv-upgrade-modal__card-label">Current</span>
            <span class="brlv-upgrade-modal__card-name">{{ currentPlanName }}</span>
            <span class="brlv-upgrade-modal__card-price">{{ currentPlanPriceDisplay }}</span>
          </div>
          <i class="fa fa-arrow-right brlv-upgrade-modal__cards-arrow" />
          <div
            class="brlv-upgrade-modal__card brlv-upgrade-modal__card--current"
            :class="{ 'brlv-upgrade-modal__card--new': selectedPlan }">
            <span class="brlv-upgrade-modal__card-label">New</span>
            <span class="brlv-upgrade-modal__card-name">{{ selectedPlan?.name }}</span>
            <span class="brlv-upgrade-modal__card-price">{{ selectedPlanPriceDisplay }}</span>
          </div>
        </div>

        <div v-if="upgradeOptions.length > 1" class="brlv-upgrade-modal__options">
          <el-radio-group v-model="selectedPlanValue" class="brlv-upgrade-modal__options-group">
            <el-radio
              v-for="option in upgradeOptions"
              :key="option.value"
              :label="option.value!"
              class="brlv-upgrade-modal__option"
            >
              <span class="brlv-upgrade-modal__option-name">{{ option.name }}</span>
              <span class="brlv-upgrade-modal__option-price">{{ formatPlanPrice(option) }}</span>
            </el-radio>
          </el-radio-group>
        </div>

        <div class="brlv-upgrade-modal__breakdown">
          <div class="brlv-upgrade-modal__breakdown-row">
            <span>{{ selectedPlan ? `${selectedPlan.name} plan` : 'Plan' }}</span>
            <span>{{ selectedPlanNumericPriceDisplay }}</span>
          </div>
          <div class="brlv-upgrade-modal__breakdown-row">
            <span>Refund for {{ refundInfo.name }} plan</span>
            <span class="brlv-upgrade-modal__breakdown-credit">-{{ refundAmountDisplay }}</span>
          </div>
          <div class="brlv-upgrade-modal__breakdown-row brlv-upgrade-modal__breakdown-row--total">
            <span>Charged today</span>
            <strong>{{ chargeTodayDisplay }}</strong>
          </div>
        </div>

        <p class="brlv-upgrade-modal__confirm-text">
          {{ confirmText }}
        </p>
      </template>
    </div>

    <template #footer>
      <p-button type="info" round :disabled="status.isLoading" @click="onCancel">
        Cancel
      </p-button>
      <p-button type="primary" round :disabled="!canConfirm || status.isLoading" @click="onConfirm">
        Confirm — charge {{ chargeTodayDisplay }}
      </p-button>
    </template>
  </el-dialog>
</template>

<script setup lang="ts">
import { computed, reactive, ref, shallowRef, watch } from 'vue';
import moment from 'moment-timezone';
import { isNil } from 'lodash';

import events from '@/events';

import util from '@/Utils/util';
import { useCustomer } from '@/use/customer';
import { useSwalDialog } from '@/use/swalDialog';

import { PlanKind } from '@/gql/graphql';
import type { CustomizedPlan } from '@/models/plan';
import type { CallerSubscriptionRefundData } from '@/models/refund';
import { planService } from '@/services/plan';
import { planCurrency } from '@/services/pricing';
import { refundService } from '@/services/refund';
import { callerPlanService } from '@/services/callerPlan';
import { defaultTimeZone } from '@/constants/app'

const props = defineProps<{
  visible: boolean;
}>();

const emit = defineEmits<{
  (event: 'update:visible', value: boolean): void;
}>();

const { customerInfo, customerActivePlan, setCustomerActivePlan } = useCustomer();
const { showSuccessPopupDialog, showErrorPopupDialog } = useSwalDialog();

// Upgrade options shown in the modal, in display order. Value monthly is the default.
const UPGRADE_PLAN_KINDS: PlanKind[] = [
  PlanKind.Value,
  PlanKind.ValueAnnual,
  PlanKind.BasicAnnual,
  PlanKind.FixedIncome,
  PlanKind.FixedIncomeAnnual,
];

const CADENCE_BY_KIND: Partial<Record<PlanKind, 'month' | 'year'>> = {
  [PlanKind.Value]: 'month',
  [PlanKind.FixedIncome]: 'month',
  [PlanKind.ValueAnnual]: 'year',
  [PlanKind.BasicAnnual]: 'year',
  [PlanKind.FixedIncomeAnnual]: 'year',
};

const status = reactive({
  isLoading: false,
  spinnerMessage: '',
});

const plans = shallowRef<CustomizedPlan[]>([]);
const refundInfo = shallowRef<CallerSubscriptionRefundData | null>(null);
const selectedPlanValue = ref<string>('');

const callerId = computed(() => customerInfo.value?.id ?? '');

const formatMoney = (price: number | null | undefined) => util.formatNullablePrice(price, planCurrency.value);

const formatPlanPrice = (plan: CustomizedPlan) =>
  formatMoney(isNil(plan.priceNumericCents) ? null : plan.priceNumericCents / 100);

const currentPlanName = computed(() => refundInfo.value?.name || customerActivePlan.value?.planName || '');
const isCurrentPlanAnnual = computed(() => (customerActivePlan.value?.planName ?? '').toLowerCase().includes('annual'));

const upgradeOptions = computed(() => plans.value
  .filter((plan) => plan.kind && UPGRADE_PLAN_KINDS.includes(plan.kind) && plan.isDefault)
  .sort(
    (a, b) =>
      UPGRADE_PLAN_KINDS.indexOf(a.kind as PlanKind) - UPGRADE_PLAN_KINDS.indexOf(b.kind as PlanKind),
  )
);

const selectedPlan = computed(() => selectedPlanValue.value ? upgradeOptions.value.find((plan) => plan.value === selectedPlanValue.value) : undefined);

const modalTitle = computed(() => selectedPlan.value ? `Upgrade to ${selectedPlan.value.name}?` : 'Upgrade plan?');

const isPlanAnnual = (plan: CustomizedPlan | null) =>
  plan?.kind ? CADENCE_BY_KIND[plan.kind] === 'year' : (plan?.value ?? '').toLowerCase().includes('annual');

const cadenceSuffix = (isAnnual: boolean) => (isAnnual ? '/yr' : '/mo');

const currentPlanPriceDisplay = computed(() =>
  refundInfo.value ? `${formatMoney(refundInfo.value.fullAmount)}${cadenceSuffix(isCurrentPlanAnnual.value)}` : '',
);

const selectedPlanPriceDisplay = computed(() => {
  if (!selectedPlan.value) {
    return '';
  }

  const price = isNil(selectedPlan.value.priceNumericCents) ? null : selectedPlan.value.priceNumericCents / 100;

  return `${formatMoney(price)}${cadenceSuffix(isPlanAnnual(selectedPlan.value))}`;
});

const selectedPlanNumericPrice = computed<number | null>(() => {
  if (isNil(selectedPlan.value?.priceNumericCents)) {
    return null;
  }
  return selectedPlan.value.priceNumericCents / 100;
});

const selectedPlanNumericPriceDisplay = computed(() => formatMoney(selectedPlanNumericPrice.value));

const refundAmount = computed(() => (refundInfo.value ? Number(refundInfo.value.refundAmount) || 0 : 0));
const refundAmountDisplay = computed(() => formatMoney(refundAmount.value));

const chargeToday = computed(() => {
  if (selectedPlanNumericPrice.value == null) {
    return null;
  }
  return Math.max(0, Math.round((selectedPlanNumericPrice.value - refundAmount.value) * 100) / 100);
});

const chargeTodayDisplay = computed(() => formatMoney(chargeToday.value));

const refundMethodText = computed(() => {
  if (!refundInfo.value) {
    return '';
  }
  return refundInfo.value.isCreditCharge
    ? 'GoGo Credits'
    : 'a credit to the issuing credit or debit card account';
});

const confirmText = computed(() => {
  if (!refundInfo.value || !selectedPlan.value) {
    return '';
  }

  const nextBillingDate = moment
    .tz(customerInfo.value?.timeZone ?? defaultTimeZone)
    .add(selectedPlan.value.billingCyclePeriodNum, selectedPlan.value.billingCyclePeriodType)
    .format('MMMM Do YYYY');

  return (
    `A Pro-rated Refund refund amount of ${refundAmountDisplay.value} will be issued as ${refundMethodText.value} ` +
    `for remaining unused portion of the "${refundInfo.value.name}" plan. ` +
    `The customer will be immediately unsubscribed and subscribed to the "${selectedPlan.value.name} ${selectedPlanNumericPriceDisplay.value}" plan. ` +
    `Next billing date: ${nextBillingDate}. ` +
    `To confirm, click the "Confirm" button.`
  );
});

const canConfirm = computed(() => !!refundInfo.value && !!selectedPlan.value && chargeToday.value != null);

const onVisibleChange = (value: boolean) => {
  emit('update:visible', value);
};

const onCancel = () => {
  emit('update:visible', false);
};

const loadData = async () => {
  status.isLoading = true;
  status.spinnerMessage = 'Loading upgrade info...';
  plans.value = [];
  refundInfo.value = null;
  selectedPlanValue.value = '';

  try {
    const callerIdValue = callerId.value;
    if (!callerIdValue) {
      return;
    }

    const [plansResponse, refundResponse] = await Promise.all([
      // No type filter: this flow offers both monthly and annual upgrade plans
      planService.getAvailablePlansForCaller({ callerId: callerIdValue }),
      refundService.getActiveSubscriptionRefundData({ callerId: callerIdValue, refundType: 'pro-rated' }),
    ]);

    plans.value = plansResponse ?? [];

    const targetPlanKind = isCurrentPlanAnnual.value ? PlanKind.ValueAnnual : PlanKind.Value;
    selectedPlanValue.value =
      plans.value.find((plan) => plan.kind === targetPlanKind && plan.isDefault)?.value ??
      plans.value.find((plan) => plan.kind === targetPlanKind)?.value;

    refundInfo.value = refundResponse;
  } catch (error) {
    const errorText = util.getGraphqlError(error, false);
    showErrorPopupDialog({ text: errorText });
    emit('update:visible', false);
  }

  status.isLoading = false;
};

const onConfirm = async () => {
  if (!canConfirm.value || !selectedPlan.value?.value) {
    return;
  }

  status.isLoading = true;
  status.spinnerMessage = 'Upgrading plan...';

  try {
    await callerPlanService.updateCallerSubscriptionWithRefund({
      callerId: callerId.value,
      planName: selectedPlan.value.value,
      refundType: 'pro-rated',
      notes: `Ride limit upgrade: ${currentPlanName.value} -> ${selectedPlan.value.name}`,
      refundAsCredits: false,
      refundAmount: null,
    });

    showSuccessPopupDialog({
      text: `Customer was upgraded to the "${selectedPlan.value.name}" plan successfully.`,
    });

    emit('update:visible', false);

    // Refresh the stored active plan so the ride limit banner recalculates
    const activePlan = await callerPlanService.fetchActiveCallerSubscription({ callerId: callerId.value });
    if (activePlan) {
      setCustomerActivePlan(activePlan);
    }

    events.emit('refresh-right-panel');
  } catch (error) {
    const errorText = util.getGraphqlError(error, false);
    showErrorPopupDialog({ text: errorText });
  }

  status.isLoading = false;
};

watch(
  () => props.visible,
  (visible) => {
    if (visible) {
      loadData();
    }
  },
);
</script>

<style scoped lang="scss">
.brlv-upgrade-modal {
  &__cards {
    display: flex;
    align-items: stretch;
    gap: 10px;
    margin-bottom: 16px;
  }

  &__cards-arrow {
    align-self: center;
    color: #909399;
  }

  &__card {
    flex: 1;
    border: 1px solid #e4e7ed;
    border-radius: 8px;
    padding: 12px 14px;
    display: flex;
    flex-direction: column;
    gap: 2px;

    &--new {
      border-color: var(--bs-primary, #409eff);
      background-color: #f5f9ff;
    }
  }

  &__card-label {
    font-size: 11px;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    color: #909399;
  }

  &__card-name {
    font-size: 16px;
    font-weight: 700;
  }

  &__card-price {
    font-size: 13px;
    color: #606266;
  }

  &__options {
    margin-bottom: 16px;
  }

  &__options-group {
    display: flex;
    flex-direction: column;
    gap: 2px;
    width: 100%;
  }

  &__option {
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
    margin-right: 0;
    height: auto;
    padding: 4px 8px;

    :deep(.el-radio__label) {
      display: flex;
      align-items: center;
      justify-content: space-between;
      flex: 1;
    }
  }

  &__option-price {
    color: #606266;
  }

  &__breakdown {
    border-top: 1px solid #ebeef5;
    padding-top: 12px;
    margin-bottom: 12px;
  }

  &__breakdown-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    font-size: 14px;
    padding: 3px 0;

    &--total {
      font-size: 15px;
      font-weight: 700;
      margin-top: 4px;
    }
  }

  &__breakdown-credit {
    color: #67c23a;
  }

  &__confirm-text {
    color: red;
    text-align: center;
    font-size: 13px;
    margin: 0;
  }
}
</style>
